import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Flex,
  IconButton,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

export interface BusinessCircleItem {
  id: string;
  title: string;
  logoURL?: string;
  subtitle?: string;
  navigateTo?: string;
}

interface BusinessCircleCarouselProps {
  items: BusinessCircleItem[];
  slidesToShow?: {
    base?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
}

const cardsBorderRadii = "8px";

const BusinessCircleCarousel: React.FC<BusinessCircleCarouselProps> = ({
  items,
  slidesToShow = { base: 3, sm: 4, md: 5, lg: 6, xl: 7 },
}) => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const slideCount = useBreakpointValue(slidesToShow);
  const slideWidth = slideCount ? `${100 / slideCount}%` : "100%";
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const { theme } = useCustomTheme();

  const checkScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    checkScroll();
    container.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      container.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [items.length]);

  const scroll = (direction: "left" | "right") => {
    const container = scrollRef.current;
    if (!container) return;

    container.scrollBy({
      left:
        direction === "left" ? -container.offsetWidth : container.offsetWidth,
      behavior: "smooth",
    });

    setTimeout(checkScroll, 300);
  };

  return (
    <Box
      position="relative"
      borderRadius={cardsBorderRadii}
      bg={theme.surface}
      border={`1px solid ${theme.border}`}
      boxShadow={theme.shadowSm}
      p={{ base: 3, md: 4 }}
    >
      {showLeftArrow && (
        <IconButton
          aria-label="Scroll Left"
          icon={<ChevronLeftIcon boxSize={6} />}
          onClick={() => scroll("left")}
          position="absolute"
          left={2}
          top="50%"
          transform="translateY(-50%)"
          zIndex={2}
          variant="ghost"
          size="sm"
          bg="blackAlpha.700"
          color="white"
          borderRadius={cardsBorderRadii}
          _hover={{ bg: "blackAlpha.800" }}
        />
      )}

      {showRightArrow && (
        <IconButton
          aria-label="Scroll Right"
          icon={<ChevronRightIcon boxSize={6} />}
          onClick={() => scroll("right")}
          position="absolute"
          right={2}
          top="50%"
          transform="translateY(-50%)"
          zIndex={2}
          variant="ghost"
          size="sm"
          bg="blackAlpha.700"
          color="white"
          borderRadius={cardsBorderRadii}
          _hover={{ bg: "blackAlpha.800" }}
        />
      )}

      <Flex
        ref={scrollRef}
        overflowX="auto"
        scrollSnapType="x mandatory"
        gap={{ base: 2, md: 3 }}
        sx={{
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {items.map((item) => (
          <Box
            key={item.id}
            flex={`0 0 ${slideWidth}`}
            scrollSnapAlign="start"
            textAlign="center"
            px={2}
            cursor="pointer"
            onClick={() =>
              navigate(
                item.navigateTo || `/search-details/businesses/${item.id}`,
              )
            }
          >
            <Flex
              direction="column"
              align="center"
              justify="center"
              gap={2}
              py={2}
            >
              <Avatar
                src={item.logoURL}
                name={item.title}
                size="lg"
                boxSize={{ base: "68px", md: "82px" }}
                border="2px solid"
                borderColor={theme.borderStrong}
                bg={theme.surfaceAlt}
              />

              <Text
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="700"
                color={theme.text}
                noOfLines={2}
                maxW="110px"
              >
                {item.title}
              </Text>
            </Flex>
          </Box>
        ))}
      </Flex>
    </Box>
  );
};

export default BusinessCircleCarousel;
